import React, { useState, useEffect, useRef } from "react";
import {
  DashboardOutlined, LineChartOutlined, UserOutlined, TeamOutlined,
  FileTextOutlined, AppstoreOutlined, CloseOutlined, LeftOutlined, RightOutlined,
  TableOutlined, LinkOutlined, InboxOutlined, UnorderedListOutlined,
  SecurityScanOutlined, BorderlessTableOutlined, EnvironmentOutlined,
  GlobalOutlined, FlagOutlined, IdcardOutlined, ApartmentOutlined, SafetyOutlined,
} from "@ant-design/icons";
import { Empty } from "antd";
import useTabsSystemStore from "../../store/RenderTabStore";
import { useThemeStore } from "../../store/useThemeStore";

/* ── helpers ─────────────────────────────────────────────────────────────── */
const tint = (hex, op = 0.08) => {
  if (!hex || hex.length < 7) return `rgba(0,0,0,${op})`;
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  return `rgba(${r},${g},${b},${op})`;
};

/* relative luminance — tells us if a color has enough contrast on dark bg */
const luminance = (hex) => {
  if (!hex || hex.length < 7) return 0;
  const r = parseInt(hex.slice(1,3),16)/255;
  const g = parseInt(hex.slice(3,5),16)/255;
  const b = parseInt(hex.slice(5,7),16)/255;
  return 0.2126*r + 0.7152*g + 0.0722*b;
};

const iconMap = {
  dashboard: <DashboardOutlined />,
  "sales-analytics": <LineChartOutlined />,
  "user-analytics": <TeamOutlined />,
  "traffic-analytics": <LineChartOutlined />,
  "monthly-reports": <FileTextOutlined />,
  "quarterly-reports": <FileTextOutlined />,
  "annual-reports": <FileTextOutlined />,
  new_vendor_registration: <TeamOutlined />,
  trigger_vendor_registration_link: <LinkOutlined />,
  vendor_registration_approval_link: <SafetyOutlined />,
  all_po: <UnorderedListOutlined />,
  inward_register: <InboxOutlined />,
  list_register: <TableOutlined />,
  role_setting: <SecurityScanOutlined />,
  menu_setting: <AppstoreOutlined />,
  user_setting: <UserOutlined />,
  uom: <BorderlessTableOutlined />,
  city: <EnvironmentOutlined />,
  state: <GlobalOutlined />,
  country: <FlagOutlined />,
  designation: <IdcardOutlined />,
  department: <ApartmentOutlined />,
  employee: <TeamOutlined />,
};

export const DynamicTabSystem = ({ selectedMenuItem, menuConfig, onTabClose, onMenuSelect }) => {
  const { openTabs, activeTab, addNewTab, setActiveTab } = useTabsSystemStore();
  const { themeColor, isDark } = useThemeStore();

  /* In dark mode, if the chosen accent is very dark it won't be visible.
     Use a brightened fallback so active tabs/borders always read clearly. */
  const activeColor = (isDark && luminance(themeColor) < 0.08)
    ? "#818cf8"   /* indigo-400 — visible on dark backgrounds */
    : themeColor;

  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    isMobile: window.innerWidth < 576,
    isTablet: window.innerWidth >= 576 && window.innerWidth < 992,
    isDesktop: window.innerWidth >= 992,
  });
  const [showLeftArrow,  setShowLeftArrow]  = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const tabContainerRef = useRef(null);
  const contentRef      = useRef(null);

  /* ── design tokens (dark / light) ──────────────────────────────────────── */
  const D = isDark ? {
    containerBg:     "#1c1c21",
    containerBorder: "rgba(255,255,255,0.06)",
    containerShadow: `0 4px 24px rgba(0,0,0,0.4)`,
    tabBarBg:        "#1c1c21",
    tabBarBorder:    "rgba(255,255,255,0.06)",
    inactiveBg:      "#27272a",
    inactiveBorder:  "rgba(255,255,255,0.10)",
    inactiveText:    "#c4c4c8",
    scrollBtnBg:     "#27272a",
    scrollBtnBorder: "rgba(255,255,255,0.14)",
    contentBg:       "#18181b",
  } : {
    containerBg:     "#ffffff",
    containerBorder: tint(themeColor, 0.10),
    containerShadow: `0 4px 24px ${tint(themeColor, 0.08)}`,
    tabBarBg:        "rgba(255,255,255,0.95)",
    tabBarBorder:    tint(themeColor, 0.08),
    inactiveBg:      "#ffffff",
    inactiveBorder:  "rgba(148,163,184,0.14)",
    inactiveText:    "#6b7280",
    scrollBtnBg:     "#ffffff",
    scrollBtnBorder: tint(themeColor, 0.14),
    contentBg:       "#ffffff",
  };

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setScreenSize({ width, isMobile: width < 576, isTablet: width >= 576 && width < 992, isDesktop: width >= 992 });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => { checkScroll(); }, [openTabs]);

  useEffect(() => {
    if (selectedMenuItem && selectedMenuItem !== activeTab) addTab(selectedMenuItem);
  }, [selectedMenuItem]);

  const checkScroll = () => {
    if (tabContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabContainerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  const addTab = (key) => {
    const tabExists = openTabs.find((tab) => tab.key === key);
    const menuItem  = menuConfig?.[key];
    if (!tabExists && menuItem) {
      const newTab = { key, label: menuItem.title || key, closable: key !== "dashboard" };
      const newTabs = [newTab, ...openTabs];
      addNewTab(newTabs);
    }
    setActiveTab(key);
  };

  const removeTab = ({ key }) => {
    const newTabs = openTabs.filter((tab) => tab.key !== key);
    if (activeTab === key) {
      const idx = openTabs.findIndex((tab) => tab.key === key);
      const next = newTabs[idx] || newTabs[idx - 1];
      setActiveTab(next?.key || "dashboard");
      onMenuSelect(next?.key ?? "dashboard");
    }
    addNewTab(newTabs);
    if (onTabClose) onTabClose(key);
  };

  const scrollTabs = (direction) => {
    if (tabContainerRef.current) {
      tabContainerRef.current.scrollBy({ left: direction === "left" ? -200 : 200, behavior: "smooth" });
      setTimeout(checkScroll, 300);
    }
  };

  const renderTabContent = (key) => {
    const customContent = menuConfig?.[key]?.content;
    return (
      <div style={{ padding: screenSize.isMobile ? "10px" : screenSize.isTablet ? "8px" : "16px", maxWidth: "100%", overflowX: "hidden", animation: "tabFadeIn 0.25s ease" }}>
        {customContent
          ? <div style={{ width: "100%" }}>{customContent}</div>
          : (
            <div style={{ padding: 48, background: isDark ? "#1c1c21" : `linear-gradient(145deg,#ffffff 0%,${tint(themeColor,0.03)} 100%)`, borderRadius: 16, border: `1px solid ${D.containerBorder}`, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 300 }}>
              <Empty description="No content available" />
            </div>
          )
        }
      </div>
    );
  };

  /* ── scroll arrow button ─────────────────────────────────────────────────*/
  const ScrollArrow = ({ direction }) => {
    const [hov, setHov] = useState(false);
    return (
      <button
        onClick={() => scrollTabs(direction)}
        style={{
          position: "absolute",
          [direction === "left" ? "left" : "right"]: 10,
          top: "50%", transform: hov ? "translateY(-50%) scale(1.06)" : "translateY(-50%)",
          width: 30, height: 30, borderRadius: 9,
          background: hov ? activeColor : D.scrollBtnBg,
          border: `1px solid ${hov ? activeColor : D.scrollBtnBorder}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", zIndex: 10,
          transition: "all 0.2s",
          boxShadow: hov ? `0 4px 14px ${tint(activeColor, 0.35)}` : "0 2px 6px rgba(0,0,0,0.08)",
        }}
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
      >
        {direction === "left"
          ? <LeftOutlined  style={{ fontSize: 12, color: hov ? "#fff" : activeColor }} />
          : <RightOutlined style={{ fontSize: 12, color: hov ? "#fff" : activeColor }} />
        }
      </button>
    );
  };

  if (openTabs.length === 0) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "88vh", background: isDark ? "#18181b" : `linear-gradient(135deg,#ffffff 0%,${tint(themeColor,0.04)} 100%)`, borderRadius: 16, border: `1px solid ${D.containerBorder}`, margin: 16 }}>
        <Empty description="No tabs open" />
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes tabFadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* scoped scrollbar — content area only */
        .tab-content-scr::-webkit-scrollbar        { width: 5px; height: 5px; }
        .tab-content-scr::-webkit-scrollbar-track  { background: transparent; border-radius: 10px; }
        .tab-content-scr::-webkit-scrollbar-thumb  { background: var(--ap); border-radius: 10px; opacity: 0.7; }
        .tab-content-scr::-webkit-scrollbar-thumb:hover { background: var(--ap); opacity: 1; }

        /* scoped scrollbar — tab strip */
        .tab-strip::-webkit-scrollbar { display: none; }
        .tab-strip { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      <div style={{
        background: D.containerBg,
        borderRadius: 8,
        minHeight: screenSize.isMobile ? "calc(100vh - 120px)" : "85vh",
        overflow: "hidden",
        width: "100%",
        border: `1px solid ${D.containerBorder}`,
        boxShadow: D.containerShadow,
        display: "flex",
        flexDirection: "column",
        transition: "background 0.3s, border-color 0.3s",
      }}>

        {/* ── Tab bar ──────────────────────────────────────────────────── */}
        <div style={{
          position: "relative",
          background: D.tabBarBg,
          borderBottom: `1px solid ${D.tabBarBorder}`,
          display: "flex", alignItems: "center",
          minHeight: 48,
          paddingLeft:  showLeftArrow  ? 44 : 8,
          paddingRight: showRightArrow ? 44 : 8,
          transition: "background 0.3s, border-color 0.3s",
        }}>

          {showLeftArrow  && <ScrollArrow direction="left"  />}

          {/* Tab strip */}
          <div className="tab-strip" ref={tabContainerRef} onScroll={checkScroll}
            style={{ display: "flex", alignItems: "center", padding: "4px 0", overflowX: "auto", overflowY: "hidden", flex: 1, WebkitOverflowScrolling: "touch", gap: 4 }}>
            {openTabs.map((tab) => {
              const icon     = menuConfig?.[tab.key]?.icon || iconMap[tab.key];
              const isActive = activeTab === tab.key;

              return (
                <div key={tab.key}
                  onClick={() => { setActiveTab(tab.key); onMenuSelect(tab.key); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 7,
                    padding: "7px 13px",
                    background: isActive ? activeColor : D.inactiveBg,
                    borderRadius: 10,
                    cursor: "pointer",
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? "#ffffff" : D.inactiveText,
                    fontSize: 13.5,
                    whiteSpace: "nowrap",
                    border: isActive ? `1px solid ${tint(activeColor, 0.3)}` : `1px solid ${D.inactiveBorder}`,
                    flexShrink: 0,
                    boxShadow: isActive ? `0 4px 14px ${tint(activeColor, 0.35)}` : "none",
                    transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
                    userSelect: "none",
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background    = isDark ? "#38383e" : "#f3f4f6";
                      e.currentTarget.style.borderColor   = tint(activeColor, 0.25);
                      e.currentTarget.style.color         = isDark ? "#f4f4f5" : "#111827";
                    } else {
                      e.currentTarget.style.boxShadow = `0 6px 20px ${tint(activeColor, 0.45)}`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background  = D.inactiveBg;
                      e.currentTarget.style.borderColor = D.inactiveBorder;
                      e.currentTarget.style.color       = D.inactiveText;
                    } else {
                      e.currentTarget.style.boxShadow = `0 4px 14px ${tint(activeColor, 0.35)}`;
                    }
                  }}
                >
                  {icon && (
                    <span style={{ fontSize: 14, display: "flex", alignItems: "center", color: "inherit", flexShrink: 0 }}>
                      {icon}
                    </span>
                  )}
                  <span style={{ letterSpacing: "-0.01em" }}>{tab.label}</span>
                  {tab.closable && (
                    <div
                      onClick={(e) => { e.stopPropagation(); removeTab(tab); }}
                      style={{
                        width: 18, height: 18,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        cursor: "pointer", marginLeft: 4, flexShrink: 0,
                        borderRadius: 5,
                        background: isActive ? "rgba(255,255,255,0.18)" : "transparent",
                        color: "inherit",
                        transition: "all 0.15s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = isActive ? "rgba(255,255,255,0.30)" : "rgba(239,68,68,0.12)";
                        if (!isActive) e.currentTarget.style.color = "#ef4444";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = isActive ? "rgba(255,255,255,0.18)" : "transparent";
                        if (!isActive) e.currentTarget.style.color = "inherit";
                      }}
                    >
                      <CloseOutlined style={{ fontSize: 10 }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {showRightArrow && <ScrollArrow direction="right" />}
        </div>

        {/* ── Content area ─────────────────────────────────────────────── */}
        <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <div className="tab-content-scr" ref={contentRef}
            style={{ flex: 1, overflowY: "auto", overflowX: "hidden", minHeight: 0, background: D.contentBg, transition: "background 0.3s" }}>
            {renderTabContent(activeTab)}
          </div>
        </div>
      </div>
    </>
  );
};
